import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { KoobInputRights } from '@koob/ui';
import { useField } from 'formik';
import { FormField, FormLabel } from 'ui/forms';
import { useAsync } from 'react-async';
import { useUserRights } from 'hooks';
import { getCountriesByOrganization } from '../../api/countries';

const Sublabel = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing()};
  color: ${({ theme }) => theme.textLight};
`;

const countryTitle = country =>
  (
    country?.translations?.find(element => element.locale === 'en') ??
    country?.translations?.[0]
  )?.title ?? '';

// Formik value stays `{ [regionId]: rightOption }` — a `{ value: null }`
// option means "no access" and is sent as `right: null` to revoke.
const RegionsRightsSelectorField = ({
  label,
  sublabel,
  name,
  organizationId,
}) => {
  const { t } = useTranslation('user');
  const rightOptions = useUserRights();
  const [{ value }, , { setValue }] = useField({ name });

  const { data, isLoading } = useAsync({
    promiseFn: organizationId ? getCountriesByOrganization : null,
    organizationId,
    watch: organizationId,
  });

  const items = (data?.data ?? []).map(country => ({
    value: country.id,
    label: countryTitle(country),
    code: country.alpha2,
    children: (country.regions ?? []).map(region => ({
      value: region.id,
      label: region.title,
    })),
  }));

  const rightsValue = Object.fromEntries(
    Object.entries(value ?? {})
      .filter(([, option]) => option?.value != null)
      .map(([regionId, option]) => [regionId, option.value]),
  );

  const handleChange = next => {
    const merged = { ...value };
    items
      .flatMap(item => item.children)
      .forEach(({ value: regionId }) => {
        const right = next[regionId] ?? null;
        if (right !== null || regionId in merged) {
          merged[regionId] = rightOptions.find(o => o.value === right);
        }
      });
    setValue(merged);
  };

  return (
    <FormField>
      {Boolean(label) && <FormLabel>{label}</FormLabel>}
      {Boolean(sublabel) && <Sublabel>{sublabel}</Sublabel>}
      <KoobInputRights
        items={items}
        rights={rightOptions.filter(o => o.value != null)}
        value={rightsValue}
        onValueChange={handleChange}
        rightPlaceholder={t('right.noAccess')}
        childrenLabel={count => t('region', { count })}
        placeholder={t('searchDestination')}
        emptyTitle={t('noCountryForThisOrganization')}
        emptySearchTitle={t('noCountryFound')}
        loading={isLoading}
        data-testid="rights"
      />
    </FormField>
  );
};

export default RegionsRightsSelectorField;
